<script lang="ts">
  import { onDestroy } from 'svelte'
  import { debounce } from 'es-toolkit'
  import { ThreeItemKeys } from '../../../../../constants/three'
  import i18n from '../../../../../i18n'
  import useObjectQueries from '../../../hooks/useObjectQueries'
  import useFileQueries from '../../../hooks/useFileQueries'
  import usePrintersQueries from '../../../hooks/usePrintersQueries'
  import Select from '../../../../../components/select/Select.svelte'
  import SectionContainer from '../SectionContainer.svelte'

  type Props = { objectIds: string[] }
  const { objectIds }: Props = $props()

  let assignedPrinterId = $state<string>()
  let isSyncingFromGroup = false

  const { useObject } = useObjectQueries()
  const { assignToPrinterMutation } = useFileQueries()

  const { usePrinters } = usePrintersQueries()
  const printersData = usePrinters()

  let fileQueries = $derived(objectIds.map(objectId => useObject(objectId)))

  // Only prefill a value when every file already shares the same printer.
  let commonPrinterId = $derived.by(() => {
    const [first, ...rest] = fileQueries.map(
      query => query.data?.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID],
    )
    if (!first || rest.some(printerId => printerId !== first))
      return undefined
    return first
  })

  const onStateUpdate = debounce(
    (targetObjectIds: string[], printerId?: string) => {
      targetObjectIds.forEach(fileId =>
        assignToPrinterMutation.mutate({ fileId, printerId }),
      )
    },
    500,
  )

  onDestroy(() => onStateUpdate.cancel())

  $effect(() => {
    isSyncingFromGroup = true
    assignedPrinterId = commonPrinterId
  })

  $effect(() => {
    const printerId = assignedPrinterId

    if (isSyncingFromGroup) {
      isSyncingFromGroup = false
      return
    }

    if (printerId !== undefined) {
      onStateUpdate(objectIds, printerId)
    }
  })
</script>

<SectionContainer
  title={$i18n.t('project.rightBar.multiSelection.title', {
    count: objectIds.length,
  })}
>
  <p>{$i18n.t('project.rightBar.multiSelection.description')}</p>
  <Select
    name="batch-printer"
    options={[
      {
        label: $i18n.t('project.rightBar.printerAssignment.noPrinter'),
        value: null,
      },
      ...(printersData.data ?? []).map(item => ({
        label: item.name,
        value: item.userData[ThreeItemKeys.PRINTER_ID],
      })),
    ]}
    placeholder={$i18n.t('project.rightBar.printerAssignment.noPrinter')}
    bind:value={assignedPrinterId}
  />
</SectionContainer>

<style lang="scss">
  p {
    color: var(--right-bar-muted-foreground);
    font-size: 0.75rem;
    line-height: 100%;
  }
</style>
